import { createPortal } from 'react-dom';
import { FieldCardSettingsContext } from './FieldCardSettingsContext';
/** Calendar summary on the canvas; host selection and tuning live in the card settings. */
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconCalendar } from '@tabler/icons-react';
import type { CalendarBookingFieldConfig, FormField } from '../../types';
import { GoogleCalendarIcon } from '../icons/GoogleCalendarIcon';
import { useSupabase } from '../../lib/SupabaseProvider';
import { useOrg } from '../../lib/OrgProvider';
import { useOrgMembersIndex } from '../../lib/useOrgMembersIndex';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import {
  DEFAULT_BOOKABLE_WINDOWS,
  type BookableWindow,
} from '../../lib/integrations/calendar/default-windows';
import { Select } from '../ui/Select';

/** Patch shape the editor emits: only calendar-booking config keys. */
type CalendarBlockPatch = Partial<
  Pick<
    CalendarBookingFieldConfig,
    | 'hostMode'
    | 'hostUserId'
    | 'connectionId'
    | 'directive'
    | 'durationMinutes'
    | 'bufferMinutes'
    | 'minimumNoticeHours'
    | 'bookingHorizonDays'
    | 'bookableWindows'
  >
>;

/** Props accepted by `CalendarBlockEditor`. */
export interface CalendarBlockEditorProps {
  /** The calendar-booking field being edited. */
  field: FormField & CalendarBookingFieldConfig;
  /** Merge a partial patch into this field. */
  onUpdateField: (patch: CalendarBlockPatch) => void;
}

/** An org-scoped calendar connection row, as the RLS-visible SELECT returns it. */
interface CalendarConnectionRow {
  id: string;
  user_id: string;
  provider: string;
  account_email: string;
  status: string;
}

/**
 * Result of the connections fetch, with guards for reconciliation.
 *
 * `failed` distinguishes a genuine empty list (fetch succeeded, no rows) from a
 * transient read failure (fetch threw). Reconciliation must never clear a saved
 * connectionId on `failed`, so a telemetry blip cannot silently unpublish a
 * live block; a genuine empty list still clears a dead stamp.
 */
interface CalendarConnectionsState {
  connections: CalendarConnectionRow[];
  loaded: boolean;
  failed: boolean;
}

/**
 * Meeting length bounds in minutes. Free-typed within the bound so a 5 minute
 * check-in or a 2 hour workshop are both authorable; the worker descriptor
 * re-validates the same range.
 */
const DURATION_MIN_MINUTES = 5;
const DURATION_MAX_MINUTES = 240;

/** Weekday numbers in display order (Monday first, Sunday last). */
const WEEKDAY_ORDER: BookableWindow['weekday'][] = [1, 2, 3, 4, 5, 6, 0];

/** Short weekday labels keyed by weekday number (0 = Sunday). */
const WEEKDAY_LABELS: Record<BookableWindow['weekday'], string> = {
  0: 'Sun',
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
};

/**
 * Provider logo for a connection row, matching the mapping the Calendars
 * settings section uses, so a calendar looks the same wherever it appears.
 * Unknown providers fall back to a generic calendar glyph.
 */
function providerIcon(provider: string): React.ReactNode {
  if (provider === 'google') return <GoogleCalendarIcon className="h-4 w-4" />;
  return <IconCalendar className="h-4 w-4" />;
}

/**
 * Fetch the org's RLS-visible calendar connections keyed for host resolution.
 * A read failure is captured and degraded to an empty list (the no-connection
 * gate), never thrown, so a telemetry blip cannot block authoring.
 */
function useOrgCalendarConnections(orgId: string | null | undefined): CalendarConnectionsState {
  const { supabase } = useSupabase();
  const [state, setState] = useState<CalendarConnectionsState>({
    connections: [],
    loaded: false,
    failed: false,
  });

  useEffect(() => {
    if (!orgId) {
      setState({ connections: [], loaded: true, failed: false });
      return undefined;
    }

    let cancelled = false;
    void (async () => {
      try {
        const { data, error } = await supabase
          .from('integration_connections')
          .select('id, user_id, provider, account_email, status')
          .eq('integration_type', 'calendar')
          .eq('organization_id', orgId);
        if (error) throw new Error(error.message);
        if (cancelled) return;
        setState({ connections: (data ?? []) as CalendarConnectionRow[], loaded: true, failed: false });
      } catch (err) {
        if (cancelled) return;
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'CalendarBlockEditor.useOrgCalendarConnections',
          { orgId },
        );
        // Degrade to the no-connection gate, but flag the failure so
        // reconciliation leaves any saved connectionId untouched.
        setState({ connections: [], loaded: true, failed: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, supabase]);

  return state;
}

/** Small auto-growing textarea with no border or fill (the directive slot). */
const BareTextarea: React.FC<{
  value: string;
  placeholder: string;
  testId: string;
  ariaLabel: string;
  onChange: (value: string) => void;
}> = ({ value, placeholder, testId, ariaLabel, onChange }) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const autoSize = useCallback((el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  useLayoutEffect(() => {
    if (ref.current) autoSize(ref.current);
  }, [value, autoSize]);
  return (
    <textarea
      ref={ref}
      rows={1}
      data-testid={testId}
      aria-label={ariaLabel}
      value={value}
      placeholder={placeholder}
      onChange={(e) => {
        onChange(e.target.value);
        autoSize(e.currentTarget);
      }}
      className="w-full resize-none overflow-hidden bg-transparent text-base font-semibold text-foreground placeholder:text-muted-foreground focus:outline-none"
    />
  );
};

/**
 * A compact numeric stepper with a quiet uppercase micro-label and a unit.
 * The value is a free-typed integer input: any in-range keystroke commits
 * live, an out-of-range or unparseable draft clamps (or reverts) on blur, so
 * an unbookable value can be neither stepped nor typed into the block.
 */
const Stepper: React.FC<{
  label: string;
  value: number;
  unit: string;
  min: number;
  max?: number;
  step: number;
  testId: string;
  onChange: (next: number) => void;
}> = ({ label, value, unit, min, max, step, testId, onChange }) => {
  // Draft holds in-progress typing (possibly out of range) so the field never
  // fights the keyboard; null means "mirror the committed value".
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={`decrease ${label}`}
          data-testid={`${testId}-dec`}
          onClick={() => onChange(Math.max(min, value - step))}
          className="flex h-7 w-7 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-surface-hover hover:text-foreground transition-colors"
        >
          <svg aria-hidden="true" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.4}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
          </svg>
        </button>
        <input
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          step={step}
          aria-label={label}
          data-testid={`${testId}-value`}
          value={draft ?? String(value)}
          onChange={(e) => {
            const raw = e.target.value;
            setDraft(raw);
            const parsed = Number(raw);
            if (
              raw.trim() !== '' &&
              Number.isInteger(parsed) &&
              parsed >= min &&
              (max === undefined || parsed <= max)
            ) {
              onChange(parsed);
            }
          }}
          onBlur={() => {
            const parsed = Number(draft ?? '');
            if (draft !== null && draft.trim() !== '' && Number.isFinite(parsed)) {
              onChange(Math.min(max ?? Infinity, Math.max(min, Math.round(parsed))));
            }
            setDraft(null);
          }}
          className="w-[4.5ch] bg-transparent text-center text-sm font-medium text-foreground focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        <span className="text-sm font-medium text-muted-foreground">{unit}</span>
        <button
          type="button"
          aria-label={`increase ${label}`}
          data-testid={`${testId}-inc`}
          onClick={() => onChange(max !== undefined ? Math.min(max, value + step) : value + step)}
          className="flex h-7 w-7 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-surface-hover hover:text-foreground transition-colors"
        >
          <svg aria-hidden="true" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.4}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
          </svg>
        </button>
      </div>
    </div>
  );
};

/**
 * Visual-first editor for a single calendar block. See module docs for the
 * host-stamping, connection-gate, and constraints contract.
 */
export const CalendarBlockEditor: React.FC<CalendarBlockEditorProps> = ({ field, onUpdateField }) => {
  const settingsContainer = useContext(FieldCardSettingsContext);
  const editing = settingsContainer !== null;
  const { user } = useSupabase();
  const { activeOrg } = useOrg();
  const currentUserId = user?.id;
  const membersIndex = useOrgMembersIndex(activeOrg?.id);
  const { connections, loaded, failed } = useOrgCalendarConnections(activeOrg?.id);

  // Active connections drive the host dropdown and stamp reconciliation.
  const activeConnections = useMemo(
    () => connections.filter((c) => c.status === 'active'),
    [connections],
  );

  const ownConnection = useMemo(
    () => (currentUserId ? activeConnections.find((c) => c.user_id === currentUserId) : undefined),
    [activeConnections, currentUserId],
  );

  // Zero-config default: a genuinely never-assigned block (no saved host and no
  // saved connection) auto-fills to the viewer's own active connection once
  // connections load. This is the only mount-time write of a host assignment;
  // everything else changes through an explicit dropdown pick.
  const ownConnectionId = ownConnection?.id;
  useEffect(() => {
    if (!loaded || failed || !currentUserId || !ownConnectionId) return;
    if (field.hostUserId || field.connectionId) return;
    onUpdateField({ hostMode: 'self', hostUserId: currentUserId, connectionId: ownConnectionId });
  }, [loaded, failed, currentUserId, ownConnectionId, field.hostUserId, field.connectionId, onUpdateField]);

  // One reconciliation path for any saved assignment: validate the stamped
  // connectionId against the saved host's active connection. A dead stamp
  // (revoked or deleted) is cleared so the publish gate
  // (validateCalendarBookings) flags missing_connection in agreement with the
  // visual gate; a reconnect under the same host self-heals to the new id.
  // hostUserId is never touched, so the block is never re-pointed at a
  // different host as a side effect of mounting, and a failed fetch (not a
  // genuine empty list) leaves the saved id alone.
  const savedHostConnectionId = useMemo(
    () =>
      field.hostUserId
        ? activeConnections.find((c) => c.user_id === field.hostUserId)?.id
        : undefined,
    [field.hostUserId, activeConnections],
  );
  useEffect(() => {
    if (!loaded || failed || !field.hostUserId) return;
    if ((field.connectionId ?? undefined) !== (savedHostConnectionId ?? undefined)) {
      onUpdateField({ connectionId: savedHostConnectionId });
    }
  }, [loaded, failed, field.hostUserId, field.connectionId, savedHostConnectionId, onUpdateField]);

  /** Dropdown options: every active org connection, the viewer's own marked. */
  const connectionOptions = useMemo(
    () =>
      activeConnections.map((c) => ({
        value: c.id,
        label: c.user_id === currentUserId ? `${c.account_email} (you)` : c.account_email,
        icon: providerIcon(c.provider),
      })),
    [activeConnections, currentUserId],
  );

  /**
   * Stamp the picked connection's owner and id atomically, deriving hostMode
   * from ownership so the worker and validation keep their contract without
   * the author ever choosing a mode.
   */
  const handleSelectConnection = useCallback(
    (connectionId: string) => {
      const conn = activeConnections.find((c) => c.id === connectionId);
      if (!conn) return;
      onUpdateField({
        hostMode: conn.user_id === currentUserId ? 'self' : 'teammate',
        hostUserId: conn.user_id,
        connectionId: conn.id,
      });
    },
    [activeConnections, currentUserId, onUpdateField],
  );

  // The viewer without an own active connection always keeps a path to connect
  // one; with zero active connections org-wide it is the only host control.
  const showAddCalendar = loaded && !ownConnection;
  // A saved host (someone other than the viewer) whose connection died keeps
  // being named, with no link: nobody consents on a teammate's behalf.
  const showHostGate =
    loaded &&
    Boolean(field.hostUserId) &&
    field.hostUserId !== currentUserId &&
    !savedHostConnectionId;
  const hostName = field.hostUserId
    ? membersIndex[field.hostUserId]?.name ?? membersIndex[field.hostUserId]?.email ?? 'this teammate'
    : 'this teammate';

  const selectedConnection = activeConnections.find(connection => connection.id === field.connectionId);
  const selection = ((activeConnections.length > 0 || showAddCalendar || showHostGate) && (
        <div data-testid="calendar-selection" className="flex flex-col gap-4 pr-12">
          {activeConnections.length > 0 && (
            <Select
              value={field.connectionId ?? ''}
              onChange={handleSelectConnection}
              options={connectionOptions}
              placeholder="Select calendar"
            />
          )}

          {/* Add calendar / dead-host gate. */}
          {showAddCalendar && (
            <div
              data-testid="calendar-add"
              className="rounded-lg border border-dashed border-border px-3 py-2 text-sm"
            >
              <Link
                to="/admin/settings?section=integrations"
                className="font-medium text-volt-soft hover:text-volt"
              >
                Add calendar
              </Link>
            </div>
          )}
          {showHostGate && (
            <div
              data-testid="calendar-connection-gate"
              className="rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground"
            >
              <span>{hostName} hasn't connected a calendar</span>
            </div>
          )}
        </div>
      ));

  return (
    <div data-testid="calendar-block-editor" className="flex flex-col gap-3">
      {editing ? settingsContainer ? createPortal(selection, settingsContainer) : selection
        : selectedConnection ? <div className="flex items-center gap-2 pr-8 text-sm text-muted-foreground">{providerIcon(selectedConnection.provider)}<span className="truncate">{selectedConnection.account_email}</span><span className="shrink-0">{field.durationMinutes} min</span></div>
          : activeConnections.length === 0 ? selection : <div className="flex items-center gap-2 text-sm text-muted-foreground"><IconCalendar className="h-4 w-4" /><span>Calendar</span></div>}

      {/* 2. Directive to hollie. */}
      {(activeConnections.length > 0 || Boolean(field.connectionId)) && <BareTextarea
        value={field.directive ?? ''}
        placeholder="ask when's a good time to follow up and book a 30 minute call."
        testId="calendar-directive"
        ariaLabel="Calendar booking directive"
        onChange={(value) => onUpdateField({ directive: value })}
      />}
    </div>
  );
};

/**
 * Three-dots settings surface for a calendar block: the four constraint
 * steppers and the bookable-windows editor, mounted by FieldCard's settings
 * panel. The host picker is also disclosed in that panel. Stateless beyond stepper typing drafts: it
 * reads only the field and patches through the same onUpdateField as the main
 * surface, so the two surfaces can never disagree and nothing is fetched or
 * reconciled twice.
 */
export const CalendarBlockSettings: React.FC<CalendarBlockEditorProps> = ({
  field,
  onUpdateField,
}) => {
  const windows = field.bookableWindows ?? DEFAULT_BOOKABLE_WINDOWS;

  /** Merge a patch into one weekday's window. */
  const updateWindow = useCallback(
    (weekday: BookableWindow['weekday'], patch: Partial<BookableWindow>) => {
      const base = field.bookableWindows ?? DEFAULT_BOOKABLE_WINDOWS;
      const next = base.map((w) => (w.weekday === weekday ? { ...w, ...patch } : w));
      onUpdateField({ bookableWindows: next });
    },
    [field.bookableWindows, onUpdateField],
  );

  return (
    <div data-testid="calendar-block-settings" className="flex flex-col gap-4">
      {/* Constraints. */}
      <div className="flex flex-wrap items-end gap-4">
        <Stepper
          label="duration"
          value={field.durationMinutes}
          unit="min"
          min={DURATION_MIN_MINUTES}
          max={DURATION_MAX_MINUTES}
          step={5}
          testId="calendar-duration"
          onChange={(next) => onUpdateField({ durationMinutes: next })}
        />
        <Stepper
          label="buffer"
          value={field.bufferMinutes}
          unit="min"
          min={0}
          step={5}
          testId="calendar-buffer"
          onChange={(next) => onUpdateField({ bufferMinutes: next })}
        />
        <Stepper
          label="notice"
          value={field.minimumNoticeHours}
          unit="h"
          min={0}
          max={168}
          step={1}
          testId="calendar-notice"
          onChange={(next) => onUpdateField({ minimumNoticeHours: next })}
        />
        <Stepper
          label="horizon"
          value={field.bookingHorizonDays}
          unit="d"
          min={1}
          max={30}
          step={1}
          testId="calendar-horizon"
          onChange={(next) => onUpdateField({ bookingHorizonDays: next })}
        />
      </div>

      {/* Bookable windows: always shown, pre-filled Mon-Fri 09:00-17:00, above
          a line naming the timezone the times are read in. The host connection
          (and its timezone) is not available on this surface, so the zone is
          named without inventing a specific one. */}
      <div className="flex flex-col gap-2">
        <span
          data-testid="calendar-timezone"
          className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
        >
          Times are in the host's calendar timezone
        </span>
        <div data-testid="calendar-windows" className="flex flex-col gap-2">
          {WEEKDAY_ORDER.map((weekday) => {
            const w = windows.find((entry) => entry.weekday === weekday) ?? {
              weekday,
              enabled: false,
              start: '09:00',
              end: '17:00',
            };
            return (
              <div
                key={weekday}
                data-testid="calendar-window-row"
                className="flex items-center gap-2"
              >
                <button
                  type="button"
                  aria-pressed={w.enabled}
                  aria-label={`${WEEKDAY_LABELS[weekday]} enabled`}
                  data-testid={`calendar-window-toggle-${weekday}`}
                  onClick={() => updateWindow(weekday, { enabled: !w.enabled })}
                  className={`w-12 rounded-lg border px-2 py-1 text-xs font-bold transition-colors ${
                    w.enabled
                      ? 'border-volt button-accent'
                      : 'border-border bg-background text-muted-foreground hover:border-border'
                  }`}
                >
                  {WEEKDAY_LABELS[weekday]}
                </button>
                <input
                  type="time"
                  aria-label={`${WEEKDAY_LABELS[weekday]} start`}
                  data-testid={`calendar-window-start-${weekday}`}
                  value={w.start}
                  disabled={!w.enabled}
                  onChange={(e) => updateWindow(weekday, { start: e.target.value })}
                  className="rounded-lg border border-border bg-background px-2 py-1 text-sm text-foreground focus:border-volt focus:outline-none disabled:opacity-50"
                />
                <span className="text-xs text-muted-foreground">to</span>
                <input
                  type="time"
                  aria-label={`${WEEKDAY_LABELS[weekday]} end`}
                  data-testid={`calendar-window-end-${weekday}`}
                  value={w.end}
                  disabled={!w.enabled}
                  onChange={(e) => updateWindow(weekday, { end: e.target.value })}
                  className="rounded-lg border border-border bg-background px-2 py-1 text-sm text-foreground focus:border-volt focus:outline-none disabled:opacity-50"
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CalendarBlockEditor;
